import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Package, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { products } from '../mock';
import { motion } from 'framer-motion';
import { useImages } from '../hooks/useImages';

const fadeUp = { hidden: { opacity: 0, y: 28 }, visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.12, ease: [0.25, 0.46, 0.45, 0.94] } }) };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };

const OrganicHues = () => {
  const organicProducts = products.filter(p => p.brand === 'organic-hues');
  const siteImages = useImages();
  const img = (slug, fb) => siteImages[slug] || fb;

  const categories = [
    { name: 'Traditional Meals', count: '25+' },
    { name: 'Cold-Pressed Oils', count: '12+' },
    { name: 'Organic Snacks', count: '30+' },
    { name: 'Health Powders', count: '18+' }
  ];

  const features = ['Farm-to-Fork Freshness', 'Zero Chemical Processing', 'Women-led Production', 'FSSAI Certified', 'Subscription Boxes Available', 'B2B Bulk Supply'];

  return (
    <div className="min-h-screen" style={{ background: '#FDF8F0' }}>
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#5C7A3A]/8 to-[#D4A574]/5"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" initial="hidden" animate="visible" variants={stagger}>
            <div className="space-y-6">
              <motion.span variants={fadeUp} custom={0} className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-[#C4844A] bg-[#C4844A]/8 px-4 py-2 rounded-full border border-[#C4844A]/15">
                Traditional Food &middot; Modern Health
              </motion.span>
              <motion.h1 variants={fadeUp} custom={1} className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-[#2C1810]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Organic <span className="text-[#5C7A3A]">Hues</span>
              </motion.h1>
              <motion.p variants={fadeUp} custom={2} className="text-base text-[#5a4535] leading-relaxed max-w-lg">
                Rediscover the authentic flavors of India with our farm-to-fork organic products. From traditional meals to cold-pressed oils, every product tells a story of purity.
              </motion.p>
              <motion.div variants={fadeUp} custom={3} className="flex flex-col sm:flex-row gap-3">
                <Button size="lg" onClick={() => window.open('mailto:nistalas211221@gmail.com?subject=Shop Products Inquiry', '_blank')} className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white font-semibold px-8 py-5 rounded-lg shadow-lg shadow-[#5C7A3A]/20 transition-all duration-300 hover:scale-105 cursor-pointer" data-testid="organic-shop-btn">
                  Shop Products <ArrowRight className="ml-2" size={18} />
                </Button>
                <Button size="lg" variant="outline" onClick={() => window.open('mailto:nistalas211221@gmail.com?subject=Franchise Inquiry', '_blank')} className="border-2 border-[#5C7A3A] text-[#5C7A3A] hover:bg-[#5C7A3A] hover:text-white font-semibold px-8 py-5 rounded-lg transition-all duration-300 hover:scale-105 cursor-pointer" data-testid="organic-franchise-btn">
                  Franchise Inquiry
                </Button>
              </motion.div>
            </div>
            <motion.div variants={fadeUp} custom={2} className="grid grid-cols-2 gap-3">
              <img src={img('organic-hero-1', 'https://images.unsplash.com/photo-1728910156510-77488f19b152')} alt="Traditional Indian food" className="w-full h-56 object-cover rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-500" />
              <img src={img('organic-hero-2', 'https://images.unsplash.com/photo-1659822887922-c1386185cc6b')} alt="Organic products" className="w-full h-56 object-cover rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-500 mt-8" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-8" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="flex items-center gap-5 justify-center mb-3">
              <div className="h-px bg-gradient-to-r from-transparent to-[#D4A574] w-16"></div>
              <Leaf size={16} className="text-[#D4A574]" />
              <div className="h-px bg-gradient-to-l from-transparent to-[#D4A574] w-16"></div>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-[#2C1810]" style={{ fontFamily: 'Playfair Display, serif' }}>Product Categories</motion.h2>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {categories.map((cat, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1, duration: 0.5 }}>
                <Card className="group border border-[#E8DFD3] shadow-sm hover:shadow-lg hover:shadow-[#D4A574]/10 transition-all duration-500 hover:-translate-y-2 bg-[#FDF8F0]" data-testid={`category-${idx}`}>
                  <CardContent className="p-6 text-center">
                    <Package size={32} className="mx-auto mb-3 text-[#5C7A3A] group-hover:scale-110 transition-transform duration-300" />
                    <h3 className="font-bold text-[#2C1810] mb-1 text-sm">{cat.name}</h3>
                    <p className="text-2xl font-bold text-[#C4844A]" style={{ fontFamily: 'Playfair Display, serif' }}>{cat.count}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-12" style={{ background: '#FDF8F0' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-8" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-[#2C1810]" style={{ fontFamily: 'Playfair Display, serif' }}>Featured Products</motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {organicProducts.map((product, idx) => (
              <motion.div key={product.id} initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1, duration: 0.5 }}>
                <Card className="group border border-[#E8DFD3] shadow-sm hover:shadow-xl hover:shadow-[#D4A574]/10 transition-all duration-500 hover:-translate-y-2 overflow-hidden bg-white" data-testid={`product-${product.id}`}>
                  <div className="relative h-52 overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2C1810]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <CardContent className="p-5">
                    <span className="text-xs font-semibold text-[#C4844A] bg-[#C4844A]/8 px-2.5 py-1 rounded-full">{product.category}</span>
                    <h3 className="text-base font-bold text-[#2C1810] mt-2.5 mb-1.5 group-hover:text-[#5C7A3A] transition-colors duration-300">{product.name}</h3>
                    <p className="text-sm text-[#7a6555] leading-relaxed">{product.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl sm:text-4xl font-bold text-[#2C1810] mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>Why Choose Organic Hues</motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feature, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, x: -15 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.08, duration: 0.4 }} className="flex items-start space-x-3 group" data-testid={`feature-${idx}`}>
                <CheckCircle size={20} className="text-[#5C7A3A] mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                <p className="text-base text-[#2C1810] font-medium">{feature}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-14 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2C4A1A] via-[#3B5A2B] to-[#2A3F1E]"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Start Your <span className="text-[#D4A574]">Organic Journey</span></h2>
            <p className="text-base text-white/75 mb-8 max-w-md mx-auto">Subscribe to our monthly boxes or explore franchise opportunities</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" onClick={() => window.open('mailto:nistalas211221@gmail.com?subject=Subscribe to Monthly Boxes', '_blank')} className="bg-[#D4A574] hover:bg-[#C4844A] text-white font-semibold px-8 py-5 rounded-lg shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer" data-testid="organic-subscribe-btn">Subscribe Now</Button>
              <Button size="lg" variant="outline" onClick={() => window.open('mailto:nistalas211221@gmail.com?subject=Franchise Inquiry', '_blank')} className="border-2 border-white text-white hover:bg-white hover:text-[#2C1810] font-semibold px-8 py-5 rounded-lg transition-all duration-300 hover:scale-105 cursor-pointer" data-testid="organic-franchise-cta-btn">Franchise With Us</Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default OrganicHues;
